import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { counterpartyKeys } from '@/services/query-keys';
import { counterpartyService } from '@/services/counterparty/counterparty-service';
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect';
import type { CounterpartyOption } from '@/types/counterparty.types';

const PAGE_SIZE = 50;

/**
 * How a counterparty reads in any picker, tab or filter trigger. Deactivated
 * ones are offered where the surface allows it (the list filter), never silently
 * indistinguishable from a live one — so every surface that names a counterparty
 * goes through here, including the one that resolves the name from elsewhere.
 */
export const counterpartyOptionLabel = (name: string, isActive: boolean): string =>
  isActive ? name : `${name} (inactive)`;

/**
 * LR-572: source for the counterparty pickers (billing, the Collateral Manager tab
 * menu). The registry runs to ~16k rows on the stands, so the server searches and
 * we pull a window at a time —
 * replacing the "ask for 10000 and filter in the browser" fetch that left every
 * selector on the page empty for the whole round-trip.
 *
 * `activeOnly` picks which slice, and the two billing surfaces genuinely differ:
 * the list filter narrows historical documents, so it must still reach a since
 * deactivated counterparty, while preview and generate offer the active slice
 * only (LR-510 dp #4 — the serializer stays permissive, so a final statement for
 * a just-closed counterparty remains bookable by id). Deactivated rows, where
 * offered, are labelled through `counterpartyOptionLabel`.
 */
export const useCounterpartySearch = (
  search: string = '',
  activeOnly: boolean = false,
  {
    eligible,
    enabled = true,
  }: {
    /** Narrows the loaded rows client-side, for a picker some rows can never be picked in. */
    eligible?: (counterparty: CounterpartyOption) => boolean;
    /** Off while the caller would not show the result, e.g. an empty box that lists other rows. */
    enabled?: boolean;
  } = {},
) => {
  const query = useInfiniteQuery({
    queryKey: counterpartyKeys.search(search, activeOnly),
    enabled,
    // The page param is a row cursor, not a page index: a short page would make
    // `index * PAGE_SIZE` skip rows the server never handed over.
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      counterpartyService.searchCounterparties({
        search,
        limit: PAGE_SIZE,
        offset: pageParam,
        activeOnly,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // An empty page ends the paging whatever `count` says: the total is read
      // per request and can drift under a concurrent write, and trusting it
      // alone would leave `hasNextPage` true while every further fetch returns
      // nothing — one scroll to the bottom then loops.
      if (lastPage.results.length === 0) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
    // The registry is near-static, so a focus refetch buys nothing, and one that
    // failed would flip the query to `error` while its pages are still cached.
    // The two hooks that do refetch on focus (`useTradesInfinite`,
    // `useTradesSummary`) poll the blotter on purpose (LR-532) — not this.
    refetchOnWindowFocus: false,
  });

  const options = useMemo<SearchableSelectOption[]>(() => {
    // Deduplicated by id: offset paging over a registry someone is editing can
    // hand the same row back on the next page (an insert earlier in the `name`
    // ordering shifts everything down), and React keys off `value`.
    // NOTE: this covers the duplicate half of that drift only — a delete earlier
    // in the ordering skips a row instead, and the skipped one stays invisible
    // until the query is retyped. Ceiling of offset paging; the upgrade is keyset
    // paging on `name` (unique, and already the server's ordering).
    const byId = new Map<string, SearchableSelectOption>();
    for (const page of query.data?.pages ?? []) {
      for (const cp of page.results) {
        if (eligible && !eligible(cp)) continue;
        const value = String(cp.id);
        if (!byId.has(value)) {
          byId.set(value, { value, label: counterpartyOptionLabel(cp.name, cp.isActive) });
        }
      }
    }
    return [...byId.values()];
  }, [query.data, eligible]);

  return { ...query, options };
};
